<template>
<fieldset>
    <div v-if="index === 0">
        <div class="text"></div>
        <div>
            <label class="required">
                Pipe Size
            </label>
        </div>
        <div>
            <label class="required">
                Pipe Type
            </label>
        </div>
        <div class="text" v-show="!mainBranch">
            <label class="required">
                Pipe Length
            </label>
        </div>
        <div class="text" v-show="mainBranch && ['lateral'].includes(orientation)">
            <label class="required">
                Lateral Pipe Length
            </label>
        </div>
        <div class="text" v-show="mainBranch && ['longitudinal'].includes(orientation)">
            <label class="required">
                Longitudinal Pipe Length
            </label>
        </div>
        <div class="text" v-show="mainBranch && ['riser-wall', 'riser-corner'].includes(orientation)">
            <label class="required">
                Vertical Pipe Length
            </label>
        </div>
        <div class="text">
            <label class="required">
                Pipe Weight
            </label>
        </div>
    
        <div class="text">
            <label class="required">
                Total Pipe Weight
            </label>
        </div>
        <div class="row-edit"></div>
    </div>
    <div>
        <div class="text">
            <label v-text="name"></label>
        </div>
        <div>
            <div class="select">
                <select :value="localData.pipeName" @input="setPipeName">
                    <option value="" v-text="loadingText"></option>
                    <option v-for="option in pipes" :value="option.name" v-text="option.name" :disabled="option.disabled"></option>
                </select>
            </div>
        </div>
        <div>
            <div class="select">
                <select :value="localData.pipeType" @input="setPipeType">
                    <option value="" v-text="loadingText"></option>
                    <option v-for="option in types" :value="option.type" v-text="option.type" :disabled="option.disabled"></option>
                </select>
            </div>
        </div>
        <div class="text" v-show="!mainBranch">
            <input-number :mToFt="useMetric(2)" id="pipe-length" v-model="localData.pipeLength" :min="0.001" :defaultMin="1" required />
        </div>
        <div class="text" v-show="mainBranch && ['lateral'].includes(orientation)">
            <input-number :mToFt="useMetric(2)" id="lateral-pipe-length" v-model="localData.lateralPipeLength" :min="lateralMin" :max="lateralMax" required />
        </div>
        <div class="text" v-show="mainBranch && ['longitudinal'].includes(orientation)">
            <input-number :mToFt="useMetric(2)" id="longitudinal-pipe-length" v-model="localData.longitudinalPipeLength" :min="longitudinalMin" :max="longitudinalMax" required />
        </div>
        <div class="text" v-show="mainBranch && ['riser-wall', 'riser-corner'].includes(orientation)">
            <input-number :mToFt="useMetric(2)" id="vertical-pipe-length" v-model="localData.verticalPipeLength" :min="0.001" :max="25" required />
        </div>
        <div class="text">
            <span v-if="record.weightPerFoot" v-text="lbPerFootOrKgPerM(record.weightPerFoot, true, 2)"></span>
        </div>
        <div class="text">
            <span v-if="record.weight" v-text="lbOrKg(record.weight, true, false, 2)"></span>
        </div>
        <div class="row-edit">
            <i v-if="can.delete" class="fa fa-trash" aria-hidden="true" @click="removeSegment"></i>
        </div>
    </div>
    <div v-show="isRiserNipple">
        <div>
            <div class="error simple inline-feedback" v-if="riserNippleError" v-text="riserNippleError"></div>
            <div class="inline-feedback" v-text="riserNippleCalculationText"></div>
        </div>
    </div>    
    <div v-if="pipeNote">
        <div>
            <div class="note" v-text="pipeNote"></div>
        </div>
    </div>
</fieldset>
</template>

<script>
import { mapKeyedGetters } from '../../../common/vuex-extra';
import { mapGetters, mapState } from 'vuex';
import Fraction from 'fraction.js';
import InputNumber from 'vue/common/_inputNumber';

const ALPHA = [
    'a','b','c','d','e','f','g',
    'h','i','j','k','l','m','n',
    'o','p','q','r','s','t','u',
    'v','w','x','y','z'
];


export default {
    name: 'submittal-line-segment',

    components: {
        InputNumber,
    },
    props: [
        'uid',
        'lateralLimit',
        'longitudinalLimit',
        'mainBranch',
        'riserNippleCalculation'
    ],
    data() {
        return {
            localData: {}
        };
    },
    mounted() {
        this.localData = Object.assign({}, this.record);
        this.setMainPipeType();
    },
    watch: {
        localData: {
            handler() {
                this.$store.dispatch('submittalSystem/setSegment', this.localData);
            },
            deep: true
        },
    },
    computed: {
        ...mapState({
            "pipesLoaded": state => state.submittalPipes.loaded
        }),
        ...mapKeyedGetters({
            "record": "submittalSystem/segment",
            "index": "submittalSystem/segmentIndex",
            "siblings": "submittalSystem/segmentSiblings"
        }),
        ...mapGetters({
            "systemMaterial": "submittalSystem/systemMaterial",
            "largestMainBranchPipeSize": "submittalSystem/largestMainBranchPipeSize",
            "mainPipeSize": "submittalSystem/mainPipeSize",
            "orientation": "submittalSystem/orientation",
            "uniqueSizes": "submittalPipes/uniqueSizes",
            "allTypes": "submittalPipes/types"
        }),
        loadingText() {
            return !this.pipesLoaded ? 'Loading' : 'Select One…';
        },
        longitudinalMin() {
            return 0.001;
        },
        lateralMin() {
            return 0.001;
        },
        lateralMax() {
            let max = this.lateralLimit;
            if (!this.localData.lateralPipeLength) {
                return max;
            }
            let current = Number(this.localData.lateralPipeLength);
            return max + current;
        },
        longitudinalMax() {
            let max = this.longitudinalLimit;
            if (!this.localData.longitudinalPipeLength) {
                return max;
            }
            let current = Number(this.localData.longitudinalPipeLength);
            return max + current;
        },
        types() {
            let disabled = false;
            let checkSize = (this.localData.pipeSize) ? this.localData.pipeSize : this.maxPipeSize;
            let options = [];
            let bySize = (checkSize) ? this.$store.getters['submittalPipes/typesFromSize'](checkSize) : [];

            let lineSegmentComponent = this;

            this.allTypes.forEach(function(type) {
                disabled = false;

                if (bySize.length && !bySize.includes(type)) {
                    disabled = true;
                }

                if (!lineSegmentComponent.hasMainPipe) {
                    if (!lineSegmentComponent.validPipeType(type)) {
                        disabled = true;
                    }
                }
                options.push({'type':type, 'disabled': disabled});
            });

            return options;
        },
        pipes() {
            let allPipes = this.uniqueSizes;
            let validTypes = this.pipeSizesByType();

            // 3 1/2" not valid for main branch
            if (this.mainBranch) {
                validTypes = validTypes.filter(s => parseFloat(s.size) != 3.5);
            }

            // Don't show lower than size 1 in the Main,
            // Main segments and in Riser Nipple sizes
            if (this.mainBranch || this.record.riserNipple) {
                validTypes = validTypes.filter(s => parseFloat(s.size) >= 1);
            }

            // Branch segments can have a max size of 3
            if (!this.mainBranch && !this.record.riserNipple) {
                validTypes = validTypes.filter(s => parseFloat(s.size) <= 3);
            }

            // Segments can't exceed the Main
            if (!this.mainBranch || this.mainBranch && this.index !== 0 && !this.record.riserNipple) {
                validTypes = validTypes.filter(s => parseFloat(s.size) <= this.mainPipeSize);
            }

            let disabled = false;
            let options = [];
            let max = this.maxPipeSize;
            let matchType = false;

            allPipes.forEach(function(pipe){
                disabled = false;

                if (validTypes.length) {
                    matchType = validTypes.some(i => {
                        if (max && parseFloat(i.size) > max) {
                            return false; // pipe size can't exceed the max
                        }

                        return i.name == pipe.name;
                    });

                    if (!matchType) {
                        disabled = true;
                    }
                }

                options.push({'name':pipe.name, 'size':parseFloat(pipe.size), 'disabled': disabled});
            });

            return options.sort((a, b) => a.size - b.size);
        },
        maxPipeSize() {
            // Riser nipples can have a max size of the largest size in the Main
            if (this.record.riserNipple === true) {
                return this.mainPipeSize;
            }

            if (this.index === 0) {
                return null;
            }

            return this.mainPipeSize;
        },
        hasMainPipe() {
            return (this.mainBranch && this.index == 0);
        },
        name() {
            if (this.isRiserNipple) {
                return 'Riser Nipple';
            }
            if (this.mainBranch && this.index === 0) {
                return 'Main';
            }

            return ALPHA[this.index] ? ALPHA[this.index].toUpperCase() : null;
        },
        can() {
            return {
                delete: !(this.mainBranch && (this.record.riserNipple || this.index === 0))
            };
        },
        isRiserNipple() {
            return this.localData.riserNipple;
        },
        riserNippleCalculationText() {
            if (this.isRiserNipple && this.riserNippleCalculation) {
                let s = (this.useMetric(2)) ? 'Mpa' : 'psi';

                return 'Riser nipple stress is ' + this.riserNippleCalculation.fy + ' ' + s + ' and may not exceed ' + this.riserNippleCalculation.yield + ' ' + s + '.';
            }
            return '';
        },
        riserNippleError() {
            if (this.isRiserNipple && this.riserNippleCalculation && this.riserNippleCalculation.error) {
                return 'Riser Nipple and Branch Line Require Further Bracing';
            }
            return '';
        },
        pipeTypesForSystemMaterial() {
            if (this.hasMainPipe) {
                return this.allTypes;
            } else {
                return this.$store.getters['submittalPipes/typesFromMaterial'](this.systemMaterial);
            }
        },
        pipeNote() {
            if (this.localData.pipeSize >= 8 && this.localData.pipeType == 'Steel Sch 10') {
                return 'Note: Schedule 10 pipe per NFPA 13 requires a minimum wall thickness of 0.188” for 8”, 10”, and 12” NPS pipe.';
            }

            return '';
        }
    },
    methods: {
        pipeSizesByType() {
            if (this.localData.pipeType) {
                return this.$store.getters['submittalPipes/sizesFromType'](this.localData.pipeType);
            }

            return this.uniqueSizes;
        },
        setPipeSize(name) {
            let pipes = this.pipeSizesByType();
            let chosen = pipes.filter(i => i.name == name).map(i => parseFloat(i.size));

            this.$set(this.localData, 'pipeSize', chosen[0]);
        },
        setPipeName($event) {
            this.$set(this.localData, 'pipeName', $event.target.value);

            this.setPipeSize($event.target.value);
        },
        setPipeType($event) {
            this.$set(this.localData, 'pipeType', $event.target.value);

            this.setPipeSize(this.localData.pipeName);

            this.setMainPipeType();
        },
        setMainPipeType() {
            if (this.hasMainPipe) {
                let payload = {
                    name: this.localData.pipeName,
                    type: this.localData.pipeType
                };
                this.$store.dispatch('submittalSystem/setMainPipeType', payload);
            }
        },
        removeSegment() {
            this.$store.dispatch('submittalSystem/removeSegment', this.localData);
        },
        validPipeType(type) {
            if (this.pipeTypesForSystemMaterial.length > 0) {
                return this.pipeTypesForSystemMaterial.includes(type);
            }

            return true;
        },
        toFrac: n => (new Fraction(n)).toFraction(true),
    }
}
</script>